/**
 * productService.js — Centralized Firestore product operations using SUBCOLLECTION
 * 
 * Path: users/{uid}/products/{productId}
 * 
 * This replaces the old approach of storing all products in a single array
 * inside the user document (which hit the 1MB Firestore limit).
 */

import { db } from '../firebase';
import {
  collection, doc, onSnapshot, setDoc, deleteDoc,
  writeBatch, serverTimestamp, getDocs, getDoc, query, updateDoc, deleteField
} from 'firebase/firestore';

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/** Deep sanitize: replace undefined with null for Firestore compatibility */
const sanitize = (obj) => {
  if (obj === undefined) return null;
  if (obj === null) return null;
  if (Array.isArray(obj)) return obj.map(sanitize);
  if (obj instanceof Date) return obj;
  if (typeof obj === 'object') {
    // ★ FIX (SPLIT V2 audit 2026-07): JANGAN rentas objek SDK Firestore
    //   (serverTimestamp/FieldValue/Timestamp/deleteField — constructor bukan
    //   Object biasa). Sebelum ini sentinel serverTimestamp DILEBUR jadi map
    //   sampah {_methodName} → lastUpdated production rosak → invalidation
    //   cache & precedence newer-wins tak berfungsi. Lepaskan as-is.
    if (obj.constructor && obj.constructor !== Object) return obj;
    const out = {};
    for (const k in obj) out[k] = sanitize(obj[k]);
    return out;
  }
  return obj;
};

// Per-browser isolation: UI flags must NEVER hit Firestore — otherwise
// opening the same account in multiple Chrome profiles cross-syncs UI state
// (checkboxes, "running" spinners). WorkspaceView.saveToCloud already strips
// these at its own boundary; we strip again here so saves through this
// service (e.g. new-product creation in App.jsx) stay clean too.
const UI_ONLY_FIELDS = ['selected', 'isGenerating', 'generatingStatus', 'generationFailed'];
// ★ COST-FIX (satu pusat, scoped 2026-07): rawOutput = salinan bertindih teks `prompts`
// (~11KB/doc ≈ 30% saiz dokumen) yang di-egress berjuta kali oleh feed listener. Untuk
// module PIPELINE, modal Full Prompt susun semula paparan dari `prompts` — salinan tak
// perlu ke Firestore. FLOWNITY & Smart Custom DIKECUALIKAN buat masa ini (modal mereka =
// tempat paste/scrape ChatGPT; tunggu fungsi susun generic extension siap release depan).
// ⚠️ KEMBAR (2026-10-13): MODUL_SIMPAN_RAWOUTPUT dalam utils/simpanHasilAgent.js + salinan dalam WorkspaceView.jsx.
const RAWOUT_KEEP_MODULES = new Set(['flownity', 'smartcustom']);
const stripUIFields = (product) => {
  const out = { ...product };
  for (const f of UI_ONLY_FIELDS) delete out[f];
  if (!RAWOUT_KEEP_MODULES.has(String(out.moduleId || '').toLowerCase())) delete out.rawOutput;
  return out;
};

// ═══════════════════════════════════════════════════════════
// ★ SPLIT V2 (projek pecah doc produk, 2026-07) — ENJIN TERAS
// Doc utama ringan (~2.4KB): metadata + prompts.setX.{uploadedVideo,
// uploadedVideoThumb, uploadedImage, captionTiktok} + marker `splitV: 2`.
// Teks berat → subdoc detail/text: semua teks prompts + intelligenceData
// + rawOutput. Feed onSnapshot jadi −91% saiz. Design penuh: memory
// project_product_doc_split + design_product_split.md (sesi 11a38637).
//
// Suis `config/product_split.enabled` gate SPLIT-WRITE sahaja (dormant
// bila OFF = perangai lama 100%). DUAL-READ (mergeDetailIntoProduct)
// SENTIASA aktif — rollback selamat. ⛔ Suis hanya ON selepas v3.8.2
// release + majoriti extension upgrade (arahan user 2026-07-23).
// ═══════════════════════════════════════════════════════════
/* ★ [2026-09-16] `captionFb` (caption Facebook, Bahagian 5) INLINE seperti captionTiktok —
   Run Facebook membacanya daripada doc ringan tanpa hidrat. ⚠️ KEMBAR: App.tsx
   INLINE_PROMPT_FIELDS + utils/tulisKadPanel.ts MEDAN_DOC_UTAMA (extension).
   ★ [2026-09-27] + `captionFbKosong` (boolean: pelanggan sengaja mahu FB tanpa caption —
   utils/captionKad.js). WAJIB inline: di sisi berat, boolean tidak menanda `hasHeavy`,
   jadi ia HILANG senyap. Kembar extension SENGAJA belum ditambah (bundled, perlu ZIP):
   panel tidak pernah menulis medan ini, dan kalau ia tercicir, FB kembali ke caption
   TikTok — tidak pernah post kosong secara senyap. */
const INLINE_PROMPT_FIELDS = new Set(['uploadedVideo', 'uploadedVideoThumb', 'uploadedImage', 'captionTiktok', 'captionFb', 'captionFbKosong']);

let _splitEnabled = false;
let _splitCfgUnsub = null;
// Dipanggil auto oleh subscribeToProducts (titik pos-auth semua view lalui).
// Fail-open: apa-apa ralat baca suis → OFF (perangai lama) + listener direset
// supaya login semula boleh buka balik.
export function subscribeSplitConfig() {
  if (_splitCfgUnsub) return;
  try {
    _splitCfgUnsub = onSnapshot(doc(db, 'config', 'product_split'), (snap) => {
      _splitEnabled = !!(snap.exists() && snap.data().enabled === true);
    }, () => {
      _splitEnabled = false;
      try { _splitCfgUnsub && _splitCfgUnsub(); } catch (_) { /* ignore */ }
      _splitCfgUnsub = null;
    });
  } catch (_) { _splitEnabled = false; }
}
export const isSplitEnabled = () => _splitEnabled;

// NOTA (2026-07-28): suis SCRAPE-DIRECT (config/scrape_direct) DIBUANG — ia dulu
// mengawal sama ada produk data-scrape boleh skip Stage 1+2. Kini Stage 1+2 OFF
// KEKAL untuk semua module (lihat WorkspaceView.generateForProduct), jadi suis tu
// dah tiada makna. Doc config/scrape_direct + rule bacaannya boleh dibiar — tiada
// kod yang baca lagi.

/** Ref subdoc teks berat sesebuah produk */
export const productDetailDoc = (uid, productId) =>
  doc(db, 'users', uid, 'products', productId, 'detail', 'text');

const _isNonEmptyStr = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Pecah objek produk → { light, heavy, hasHeavy }.
 * light  = doc utama (URL media + captionTiktok + metadata)
 * heavy  = subdoc detail/text (teks prompts + intelligenceData + rawOutput)
 * hasHeavy = ada kandungan berat SEBENAR (teks bukan-kosong / intel / rawOutput)
 *            → gate KANDUNGAN untuk tulis subdoc (bukan flag — elak tulis kosong
 *            atas subdoc berisi bila state ringan).
 */
export function splitProductForWrite(product) {
  const light = { ...product };
  const heavy = {};
  let hasHeavy = false;
  if (product.intelligenceData) {
    heavy.intelligenceData = product.intelligenceData;
    hasHeavy = true;
  }
  delete light.intelligenceData;
  if (_isNonEmptyStr(product.rawOutput)) {
    heavy.rawOutput = product.rawOutput;
    hasHeavy = true;
  }
  delete light.rawOutput;
  if (product.prompts && typeof product.prompts === 'object' && !Array.isArray(product.prompts)) {
    const lp = {};
    const hp = {};
    for (const [k, setData] of Object.entries(product.prompts)) {
      if (!setData || typeof setData !== 'object' || Array.isArray(setData)) {
        // Kunci bukan-set (cth _templateTrace) → sisi berat
        if (setData != null) { hp[k] = setData; hasHeavy = true; }
        continue;
      }
      const ls = {};
      const hs = {};
      for (const [f, v] of Object.entries(setData)) {
        if (INLINE_PROMPT_FIELDS.has(f)) { ls[f] = v; }
        else {
          hs[f] = v;
          if (_isNonEmptyStr(v)) hasHeavy = true;
          else if (v && typeof v === 'object') { hasHeavy = true; }
        }
      }
      if (Object.keys(ls).length) lp[k] = ls;
      if (Object.keys(hs).length) hp[k] = hs;
    }
    light.prompts = lp;
    if (Object.keys(hp).length) heavy.prompts = hp;
  }
  return { light, heavy, hasHeavy };
}

/**
 * DUAL-READ: cantum subdoc detail ke dalam produk ringan (untuk paparan/Run).
 * Precedence doc CAMPURAN (splitV:2 + teks inline serentak — boleh berlaku bila
 * extension/tab versi lama tulis teks inline balik): pihak lebih BARU menang
 * (lastUpdated doc utama vs updatedAt subdoc; seri → inline menang), merge
 * per-field supaya field yang hanya wujud sebelah tak hilang.
 */
export function mergeDetailIntoProduct(product, detail) {
  if (!detail || !detail.exists) return { ...product, _detailLoaded: true };
  const tsMillis = (t) => {
    try {
      if (!t) return 0;
      if (typeof t.toMillis === 'function') return t.toMillis();
      if (typeof t.seconds === 'number') return t.seconds * 1000;
      return new Date(t).getTime() || 0;
    } catch (_) { return 0; }
  };
  const inlineNewer = tsMillis(product.lastUpdated) >= tsMillis(detail.updatedAt);
  const mergedPrompts = {};
  const keys = new Set([
    ...Object.keys(product.prompts || {}),
    ...Object.keys(detail.prompts || {}),
  ]);
  for (const k of keys) {
    const inl = (product.prompts || {})[k];
    const det = (detail.prompts || {})[k];
    if (inl && det && typeof inl === 'object' && typeof det === 'object'
        && !Array.isArray(inl) && !Array.isArray(det)) {
      mergedPrompts[k] = inlineNewer ? { ...det, ...inl } : { ...inl, ...det };
    } else {
      mergedPrompts[k] = inlineNewer ? (inl ?? det) : (det ?? inl);
    }
  }
  return {
    ...product,
    prompts: mergedPrompts,
    intelligenceData: product.intelligenceData ?? detail.intelligenceData ?? null,
    rawOutput: product.rawOutput ?? detail.rawOutput ?? null,
    _detailLoaded: true,
  };
}

/**
 * Muat subdoc teks berat (getDoc on-demand — laluan hydrate).
 * exists:false = produk memang TIADA teks (sah — cth skeleton baru); BUKAN ralat.
 * Ralat rangkaian/permission → THROW — pemanggil WAJIB abort (jangan senyap).
 */
export async function loadProductDetail(uid, productId) {
  const snap = await getDoc(productDetailDoc(uid, productId));
  if (!snap.exists()) {
    return { exists: false, prompts: null, intelligenceData: null, rawOutput: null, updatedAt: null };
  }
  const d = snap.data();
  return {
    exists: true,
    prompts: d.prompts || null,
    intelligenceData: d.intelligenceData ?? null,
    rawOutput: d.rawOutput ?? null,
    updatedAt: d.updatedAt ?? null,
  };
}

/**
 * Tulis SATU produk ke batch — titik pusat SEMUA save produk.
 * Suis OFF → perangai lama (doc penuh). Suis ON → split atomik dlm batch sama:
 * doc utama ringan + splitV:2, subdoc detail (setDoc MERGE — field-level, supaya
 * payload separa TAK memadam teks set lain; padam teks hanya via retry/reset).
 * Migrate-on-write percuma: doc legacy gemuk yang di-save kod baru terus terpecah.
 */
function _stageProductWrite(batch, uid, p) {
  const clean = stripUIFields(p);
  delete clean._detailLoaded;
  if (!_splitEnabled) {
    batch.set(productDoc(uid, p.id), sanitize({ ...clean, lastUpdated: serverTimestamp() }));
    return;
  }
  const { light, heavy, hasHeavy } = splitProductForWrite(clean);
  light.splitV = 2;
  batch.set(productDoc(uid, p.id), sanitize({ ...light, lastUpdated: serverTimestamp() }));
  if (hasHeavy) {
    batch.set(productDetailDoc(uid, p.id), sanitize({ ...heavy, updatedAt: serverTimestamp() }), { merge: true });
  }
}

/** Get subcollection reference for a user's products */
const productsCol = (uid) => collection(db, 'users', uid, 'products');

/** Get document reference for a specific product */
const productDoc = (uid, productId) => doc(db, 'users', uid, 'products', productId);

// ═══════════════════════════════════════════════════════════
// READ: Subscribe to products (real-time)
// ═══════════════════════════════════════════════════════════

/**
 * Subscribe to all products for a user (real-time listener).
 * Falls back to the old `products` array if subcollection is empty.
 * @param {string} uid 
 * @param {(products: Array) => void} callback 
 * @param {(error: Error) => void} onError 
 * @returns {() => void} unsubscribe function
 */
/**
 * Check if a product is an orphan recovered product (ghost).
 * These are products re-created by storage recovery that the user had previously deleted.
 * They have status 'recovered', a generic name, and no real user-edited content.
 */
const isOrphanRecovered = (product) => {
  if (product.status !== 'recovered') return false;
  // If name starts with "Recovered Product" it was auto-generated by recovery, not user-edited
  if (product.name && product.name.startsWith('Recovered Product')) return true;
  // Also filter if recoveredFrom is global_storage_recovery and no real name/link
  if (product.recoveredFrom === 'global_storage_recovery' && !product.link && !product.imageUrl) return true;
  return false;
};

// Build the product list from a subcollection snapshot (+ legacy fallback when empty).
// Extracted so the multiplexed listener below can reuse it.
async function _resolveProducts(uid, snapshot) {
  if (!snapshot.empty) {
    // Subcollection has data — use it
    const products = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
    // ★ Tag orphan recovered products so UI can show them with warnings
    let orphanCount = 0;
    products.forEach(p => {
      if (isOrphanRecovered(p)) { p._isOrphanRecovered = true; orphanCount++; }
    });
    if (orphanCount > 0) {
      console.log(`[ProductService] Tagged ${orphanCount} orphan recovered products for user review`);
    }
    // Sort by originalIndex or number for consistent ordering
    products.sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0));
    return products;
  }
  // Subcollection empty — one-shot read user doc to decide fallback.
  // ★ If user has _migrationDone, empty subcollection = genuinely empty (do NOT
  // resurrect the legacy `products` array).
  const docSnap = await getDoc(doc(db, 'users', uid));
  if (!docSnap.exists()) return [];
  const data = docSnap.data();
  if (data._migrationDone) {
    console.log('[ProductService] Subcollection empty but user is migrated — returning empty (not resurrecting legacy array)');
    return [];
  }
  if (data.products && Array.isArray(data.products) && data.products.length > 0) {
    console.log('[ProductService] Fallback: Reading from old products array (user not yet migrated)');
    const filtered = data.products.filter(p => !isOrphanRecovered(p));
    return filtered.length > 0 ? filtered : [];
  }
  return [];
}

// ★ COST-FIX (multiplex): App.jsx, WorkspaceView, and SpaceSettingsView each call
// subscribeToProducts for the SAME user. Previously each opened its OWN onSnapshot
// on the products subcollection → 2-3× duplicate QUERY reads (the dominant Firestore
// cost). Now we keep exactly ONE underlying listener per uid and fan the data out to
// every subscriber. A new subscriber receives the last-known snapshot immediately, so
// attaching costs ZERO extra reads. The real listener is torn down when the LAST
// subscriber unsubscribes.
//
// Public API is unchanged: same (uid, callback, onError) → unsubscribe signature,
// same product-array shape, same migration fallback.
const _productSubs = new Map(); // uid -> { callbacks:Set, errbacks:Set, unsub, last, hasData }

export function subscribeToProducts(uid, callback, onError) {
  if (!uid || typeof callback !== 'function') return () => {};

  // ★ SPLIT V2: buka listener suis sekali (titik pos-auth semua view lalui)
  subscribeSplitConfig();

  let entry = _productSubs.get(uid);
  if (!entry) {
    // First subscriber for this uid → open the single real listener.
    entry = { callbacks: new Set(), errbacks: new Set(), unsub: null, last: null, hasData: false };
    _productSubs.set(uid, entry);

    entry.unsub = onSnapshot(productsCol(uid), async (snapshot) => {
      try {
        const products = await _resolveProducts(uid, snapshot);
        entry.last = products;
        entry.hasData = true;
        entry.callbacks.forEach(cb => {
          try { cb(products); } catch (e) { console.error('[ProductService] subscriber callback error', e); }
        });
      } catch (err) {
        console.error('[ProductService] Fallback read error:', err);
        entry.errbacks.forEach(eb => { try { eb(err); } catch (_) { /* ignore */ } });
      }
    }, (err) => {
      console.error('[ProductService] Subcollection read error:', err);
      entry.errbacks.forEach(eb => { try { eb(err); } catch (_) { /* ignore */ } });
    });
  }

  entry.callbacks.add(callback);
  if (onError) entry.errbacks.add(onError);

  // Deliver last-known snapshot to this new subscriber immediately (no extra read).
  // Async (microtask) to mirror onSnapshot's async delivery + avoid setState-in-render.
  if (entry.hasData) {
    const snapshotData = entry.last;
    Promise.resolve().then(() => {
      if (entry.callbacks.has(callback)) {
        try { callback(snapshotData); } catch (e) { console.error('[ProductService] late-deliver error', e); }
      }
    });
  }

  // Unsubscribe for THIS caller only. Tear down the real listener when the last leaves.
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    entry.callbacks.delete(callback);
    if (onError) entry.errbacks.delete(onError);
    if (entry.callbacks.size === 0) {
      try { entry.unsub && entry.unsub(); } catch (_) { /* ignore */ }
      _productSubs.delete(uid);
    }
  };
}

// ═══════════════════════════════════════════════════════════
// WRITE: Save/update products
// ═══════════════════════════════════════════════════════════

/**
 * Save a single product to subcollection
 * @param {string} uid
 * @param {object} product — must have .id
 */
export async function saveProduct(uid, product) {
  if (!product.id) throw new Error('Product must have an id');
  // ★ SPLIT V2: guna batch supaya doc utama + subdoc atomik (satu commit)
  const batch = writeBatch(db);
  _stageProductWrite(batch, uid, product);
  await batch.commit();
}

/**
 * Save multiple products to subcollection (batch write, max 500 per batch)
 * @param {string} uid
 * @param {Array} products
 */
export async function saveProducts(uid, products) {
  // ★ SPLIT V2: 200 produk/batch (setiap produk boleh jadi 2 ops: utama + detail;
  //   had Firestore 500 ops/batch)
  const BATCH_SIZE = 200;
  for (let i = 0; i < products.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = products.slice(i, i + BATCH_SIZE);
    for (const p of chunk) {
      if (!p.id) continue;
      _stageProductWrite(batch, uid, p);
    }
    await batch.commit();
  }
}

/**
 * Update specific fields of a single product
 * @param {string} uid
 * @param {string} productId
 * @param {object} updates — partial fields to update
 */
export async function updateProduct(uid, productId, updates) {
  const ref = productDoc(uid, productId);
  const clean = stripUIFields(updates);
  delete clean._detailLoaded;
  // ★ SPLIT V2: kalau updates bawa kandungan berat (prompts teks / intelligence /
  //   rawOutput) semasa suis ON → pecah: berat ke subdoc (merge), ringan ke doc utama.
  //   (Pemanggil semasa hanya hantar field ringan — guard masa depan.)
  if (_splitEnabled && (clean.prompts || clean.intelligenceData || clean.rawOutput)) {
    const { light, heavy, hasHeavy } = splitProductForWrite({ ...clean, id: productId });
    delete light.id;
    const batch = writeBatch(db);
    batch.update(ref, sanitize({ ...light, splitV: 2, lastUpdated: serverTimestamp() }));
    if (hasHeavy) {
      batch.set(productDetailDoc(uid, productId), sanitize({ ...heavy, updatedAt: serverTimestamp() }), { merge: true });
    }
    await batch.commit();
    return;
  }
  await updateDoc(ref, sanitize({ ...clean, lastUpdated: serverTimestamp() }));
}

/**
 * Delete a single product
 * @param {string} uid
 * @param {string} productId
 */
export async function removeProduct(uid, productId) {
  // ★ SPLIT V2: padam subdoc detail sekali (delete doc induk TAK padam
  //   subcollection → yatim). Delete doc tak wujud = no-op selamat.
  const batch = writeBatch(db);
  batch.delete(productDetailDoc(uid, productId));
  batch.delete(productDoc(uid, productId));
  await batch.commit();
}

/**
 * Delete multiple products atomically using writeBatch.
 * ★ This is critical — individual deleteDoc calls cause onSnapshot to fire N times,
 * and intermediate snapshots still contain not-yet-deleted docs, causing products
 * to reappear in the UI. Batch delete ensures a single atomic snapshot update.
 * @param {string} uid
 * @param {string[]} productIds — array of product IDs to delete
 */
export async function removeProducts(uid, productIds) {
  if (!productIds || productIds.length === 0) return;
  // ★ SPLIT V2: 2 delete per produk (detail + utama) → 200/batch
  const BATCH_SIZE = 200;
  for (let i = 0; i < productIds.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = productIds.slice(i, i + BATCH_SIZE);
    for (const id of chunk) {
      batch.delete(productDetailDoc(uid, id));
      batch.delete(productDoc(uid, id));
    }
    await batch.commit();
  }
  console.log(`[ProductService] Batch deleted ${productIds.length} products (+detail)`);
}

/**
 * Replace ALL products (delete all existing, write new ones)
 * Used for operations that rewrite the entire products array
 * @param {string} uid
 * @param {Array} products
 */
export async function replaceAllProducts(uid, products) {
  // Get all existing product IDs
  const existingSnap = await getDocs(productsCol(uid));
  
  // Build a set of new product IDs
  const newIds = new Set(products.map(p => p.id));
  
  // Delete products that are no longer in the new array
  const BATCH_SIZE = 400;
  const toDelete = existingSnap.docs.filter(d => !newIds.has(d.id));
  
  // ★ SPLIT V2: 2 delete per produk → separuhkan chunk (jaga had 500 ops/batch)
  const DELETE_CHUNK = 200;
  for (let i = 0; i < toDelete.length; i += DELETE_CHUNK) {
    const batch = writeBatch(db);
    toDelete.slice(i, i + DELETE_CHUNK).forEach(d => {
      // ★ SPLIT V2: padam subdoc detail sekali
      batch.delete(productDetailDoc(uid, d.id));
      batch.delete(d.ref);
    });
    await batch.commit();
  }
  
  // Write all new/updated products
  await saveProducts(uid, products);
}

/**
 * Sync local products to Firestore subcollection (smart diff)
 * Only writes changed products, avoids unnecessary writes
 * @param {string} uid
 * @param {Array} products — full products array from local state
 */
export async function syncProducts(uid, products) {
  await saveProducts(uid, products);
  // Also update lastUpdated on user doc
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { lastUpdated: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn('[ProductService] Failed to update user doc timestamp:', e);
  }
}

// ═══════════════════════════════════════════════════════════
// MIGRATION: Legacy cleanup (remove old array from user doc)
// ═══════════════════════════════════════════════════════════

/**
 * Remove old `products` array from user document after migration
 * @param {string} uid
 */
export async function removeLegacyProductsArray(uid) {
  const userRef = doc(db, 'users', uid);
  // Can't use deleteField in setDoc with merge, so we set it to null/delete
  // Actually, FieldValue.delete() works with updateDoc
  const { deleteField } = await import('firebase/firestore');
  await updateDoc(userRef, { products: deleteField() });
  console.log('[ProductService] Removed legacy products array from user doc');
}

// ═══════════════════════════════════════════════════════════
// ONE-TIME METADATA RECOVERY: Match recovered products with productLibrary
// ═══════════════════════════════════════════════════════════
// Recovered products only have video URLs (from Storage recovery).
// This matches them back to productLibrary items by timestamp
// to recover: name, link, imageUrl.

/**
 * One-time: match recovered/orphan products with productLibrary items
 * to restore lost metadata (name, link, imageUrl).
 * Runs once per user (gated by `_metadataRecoveryV1` flag).
 * @param {string} uid
 * @returns {Promise<{recovered: number, unmatched: number}>}
 */
export async function recoverProductMetadata(uid) {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return { recovered: 0, unmatched: 0 };

  const userData = userSnap.data();

  // Gate: only run once
  if (userData._metadataRecoveryV1) {
    console.log('[MetadataRecovery] Already recovered, skipping');
    return { recovered: 0, unmatched: 0 };
  }

  // Flatten all productLibrary items into one array
  const productLibrary = userData.productLibrary;
  const allLibItems = [];
  if (productLibrary && typeof productLibrary === 'object') {
    for (const [spaceId, items] of Object.entries(productLibrary)) {
      if (Array.isArray(items)) {
        items.forEach(item => {
          if (item && item.id) allLibItems.push(item);
        });
      }
    }
  }

  if (allLibItems.length === 0) {
    console.log('[MetadataRecovery] No productLibrary items found, marking as done');
    await setDoc(userRef, { _metadataRecoveryV1: true }, { merge: true });
    return { recovered: 0, unmatched: 0 };
  }

  // Index library items by their timestamp for fast lookup
  const libByTimestamp = allLibItems.map(lib => {
    const parts = lib.id.split('_');
    return { ...lib, ts: parseInt(parts[1]) || 0 };
  }).filter(l => l.ts > 0);

  // Get subcollection products
  const subSnap = await getDocs(productsCol(uid));
  const recoveredProducts = [];
  subSnap.docs.forEach(d => {
    const data = d.data();
    // Target: recovered products with no real name/link
    if (data.status === 'recovered' && (!data.link || data.link === '') && (!data.name || data.name.startsWith('Recovered Product'))) {
      recoveredProducts.push({ id: d.id, ref: d.ref, ...data });
    }
  });

  if (recoveredProducts.length === 0) {
    console.log('[MetadataRecovery] No recovered products to match, marking as done');
    await setDoc(userRef, { _metadataRecoveryV1: true }, { merge: true });
    return { recovered: 0, unmatched: 0 };
  }

  console.log(`[MetadataRecovery] Found ${recoveredProducts.length} recovered products and ${libByTimestamp.length} library items`);

  // Match each recovered product to closest library item by timestamp
  let recovered = 0;
  let unmatched = 0;
  const TOLERANCE_MS = 5000; // 5 second tolerance
  const BATCH_SIZE = 400;

  for (let i = 0; i < recoveredProducts.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = recoveredProducts.slice(i, i + BATCH_SIZE);

    for (const prod of chunk) {
      // Extract timestamp from product ID: prod_{timestamp}_{index} or prod_{timestamp}_{index}_{random}
      const parts = prod.id.split('_');
      const prodTs = parseInt(parts[1]) || 0;
      if (!prodTs) { unmatched++; continue; }

      // Find closest library item
      let bestMatch = null;
      let bestDiff = Infinity;
      for (const lib of libByTimestamp) {
        const diff = Math.abs(prodTs - lib.ts);
        if (diff < bestDiff && diff <= TOLERANCE_MS) {
          bestDiff = diff;
          bestMatch = lib;
        }
      }

      if (bestMatch && bestMatch.name && bestMatch.link) {
        console.log(`[MetadataRecovery] ✓ Matched ${prod.id} → "${bestMatch.name.substring(0, 50)}..." (Δ${bestDiff}ms)`);
        const ref = productDoc(uid, prod.id);
        batch.set(ref, sanitize({
          name: bestMatch.name,
          link: bestMatch.link,
          imageUrl: bestMatch.imageUrl || '',
          number: bestMatch.number || 0,
          // Keep status as recovered — user can retry pipeline to regenerate
          _recoveredMetadataFrom: bestMatch.id,
          _recoveredMetadataAt: new Date().toISOString(),
          lastUpdated: serverTimestamp()
        }), { merge: true });
        recovered++;
      } else {
        console.log(`[MetadataRecovery] ✗ No match for ${prod.id} (ts=${prodTs})`);
        unmatched++;
      }
    }

    await batch.commit();
  }

  // Mark as done
  await setDoc(userRef, { _metadataRecoveryV1: true }, { merge: true });
  console.log(`[MetadataRecovery] ✅ Done! Recovered: ${recovered}, Unmatched: ${unmatched}`);
  return { recovered, unmatched };
}

// ═══════════════════════════════════════════════════════════
// RETRY PIPELINE: Reset product to fresh state for re-generation
// ═══════════════════════════════════════════════════════════

/**
 * Reset a product to a fresh state — keeps name/link/imageUrl/videos
 * but clears all prompt text, captions, and statuses.
 * User can then re-run the full ChatGPT generation pipeline.
 * @param {string} uid
 * @param {string} productId
 * @param {object} currentProduct - the current product data
 * @returns {object} the reset product data
 */
export async function retryProductPipeline(uid, productId, currentProduct) {
  // Spread-then-override pattern: start from the existing product (preserves
  // every identity field — moduleId, avatarId, gender, logicId, intelligenceData,
  // templateSnapshot, imageBase64, scrapedData, smartcustomSceneCount, spaceId,
  // groupId, etc.) and override ONLY the AI-output / status fields.
  //
  // Whitelist-style rebuild previously dropped moduleId on retry, causing
  // ai16saat products to regenerate as 1-scene ai8saat output. Spread-style
  // is also future-proof: new product fields auto-preserve on retry.

  // Within prompts.setN: keep uploaded video files, drop generated text.
  const preservedPrompts = {};
  if (currentProduct.prompts && typeof currentProduct.prompts === 'object') {
    for (const [setKey, setData] of Object.entries(currentProduct.prompts)) {
      if (setData && typeof setData === 'object') {
        const preserved = {};
        if (setData.uploadedVideo) preserved.uploadedVideo = setData.uploadedVideo;
        if (setData.uploadedVideoThumb) preserved.uploadedVideoThumb = setData.uploadedVideoThumb;
        if (Object.keys(preserved).length > 0) {
          preservedPrompts[setKey] = preserved;
        }
      }
    }
  }

  const resetProduct = {
    ...currentProduct,
    id: productId,
    status: 'pending',
    prompts: preservedPrompts,
    chatgptUrl: null,
    rawOutput: null,
    setStatuses: null,
    setType: null,
    logs: ['Pipeline reset — ready for fresh AI generation'],
    _pipelineResetAt: new Date().toISOString(),
    lastUpdated: serverTimestamp(),
  };
  // Drop ephemeral UI flags — must never persist to Firestore.
  for (const f of UI_ONLY_FIELDS) delete resetProduct[f];
  delete resetProduct._detailLoaded;

  // ★ SPLIT V2: produk split (atau suis ON) → reset dua-dua doc ATOMIK:
  //   - subdoc: padam SELURUH teks prompts + rawOutput, KEKALKAN intelligenceData
  //     (kontrak sedia ada — retry guna semula intel utk skip Stage 1+2)
  //   - doc utama: reset ringan + splitV kekal
  if (_splitEnabled || currentProduct.splitV === 2) {
    const { light, heavy, hasHeavy } = splitProductForWrite(resetProduct);
    light.splitV = 2;
    const batch = writeBatch(db);
    batch.set(productDoc(uid, productId), sanitize(light));
    batch.set(productDetailDoc(uid, productId), {
      prompts: deleteField(),
      rawOutput: deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    // Kalau resetProduct sendiri bawa intel inline (doc legacy gemuk baru migrate-on-write)
    // → pindahkan ke subdoc sekali
    if (hasHeavy && heavy.intelligenceData) {
      batch.set(productDetailDoc(uid, productId), sanitize({ intelligenceData: heavy.intelligenceData }), { merge: true });
    }
    await batch.commit();
    console.log(`[RetryPipeline] ✓ Reset (SPLIT) product ${productId} — teks subdoc dipadam, intelligence dikekalkan`);
    return resetProduct;
  }

  const ref = productDoc(uid, productId);
  await setDoc(ref, sanitize(resetProduct));

  console.log(`[RetryPipeline] ✓ Reset product ${productId} to fresh state (module preserved: ${currentProduct.moduleId || 'none'})`);
  return resetProduct;
}

// ═══════════════════════════════════════════════════════════
// ONE-TIME STATUS SYNC: User Doc → Subcollection
// ═══════════════════════════════════════════════════════════
// Fix for bug where handleRunFlow/handleRunTikTok wrote status
// to user doc instead of subcollection. This ensures the
// subcollection reflects what the user actually left behind.
// Only syncs STATUS-related fields (not prompts/name/link which
// may be more recent in subcollection).

const STATUS_SYNC_FIELDS = ['status', 'selected', 'setStatuses', 'tab', 'extensionSettings'];

/**
 * One-time sync: merge status fields from user doc products[] into subcollection.
 * Runs once per user (gated by `_statusSyncV1` flag).
 * @param {string} uid
 * @returns {Promise<{synced: number, skipped: number}>}
 */
export async function syncStatusFromUserDoc(uid) {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    console.log('[StatusSync] No user doc found, skipping');
    return { synced: 0, skipped: 0 };
  }

  const userData = userSnap.data();

  // Gate: only run once
  if (userData._statusSyncV1) {
    console.log('[StatusSync] Already synced, skipping');
    return { synced: 0, skipped: 0 };
  }

  const userDocProducts = userData.products;
  if (!userDocProducts || !Array.isArray(userDocProducts) || userDocProducts.length === 0) {
    console.log('[StatusSync] No products in user doc, marking as done');
    await setDoc(userRef, { _statusSyncV1: true }, { merge: true });
    return { synced: 0, skipped: 0 };
  }

  // Filter: only active products (not recovered/orphan)
  const activeProducts = userDocProducts.filter(p => {
    if (!p || !p.id) return false;
    if (p.status === 'recovered') return false;
    if (p.recoveredFrom === 'global_storage_recovery' && !p.link && !p.imageUrl) return false;
    return true;
  });

  if (activeProducts.length === 0) {
    console.log('[StatusSync] No active products in user doc, marking as done');
    await setDoc(userRef, { _statusSyncV1: true }, { merge: true });
    return { synced: 0, skipped: 0 };
  }

  console.log(`[StatusSync] Found ${activeProducts.length} active products in user doc. Syncing status to subcollection...`);

  // Read current subcollection docs
  const subSnap = await getDocs(productsCol(uid));
  const subMap = new Map();
  subSnap.docs.forEach(d => subMap.set(d.id, d.data()));

  let synced = 0;
  let skipped = 0;
  // ★ SPLIT V2: _stageProductWrite boleh 2 ops/produk bila suis ON → chunk 200
  const BATCH_SIZE = 200;

  for (let i = 0; i < activeProducts.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = activeProducts.slice(i, i + BATCH_SIZE);

    for (const userProduct of chunk) {
      const subProduct = subMap.get(userProduct.id);

      if (!subProduct) {
        // Product exists in user doc but NOT in subcollection — write full product
        // ★ SPLIT V2: lalu titik pusat supaya salinan legacy pun terpecah bila suis ON
        console.log(`[StatusSync] Product ${userProduct.id} missing from subcollection — writing full copy`);
        _stageProductWrite(batch, uid, { ...userProduct, _syncedFromUserDoc: true });
        synced++;
        continue;
      }

      // Compare status-related fields only
      const updates = {};
      let hasChanges = false;

      for (const field of STATUS_SYNC_FIELDS) {
        const userVal = userProduct[field];
        const subVal = subProduct[field];

        // Only sync if user doc has a value and it differs from subcollection
        if (userVal !== undefined && userVal !== null && JSON.stringify(userVal) !== JSON.stringify(subVal)) {
          updates[field] = userVal;
          hasChanges = true;
        }
      }

      if (hasChanges) {
        console.log(`[StatusSync] Patching ${userProduct.id}:`, Object.keys(updates).join(', '));
        const ref = productDoc(uid, userProduct.id);
        batch.set(ref, sanitize({ ...updates, _syncedFromUserDoc: true, lastUpdated: serverTimestamp() }), { merge: true });
        synced++;
      } else {
        skipped++;
      }
    }

    await batch.commit();
  }

  // Mark as done
  await setDoc(userRef, { _statusSyncV1: true }, { merge: true });
  console.log(`[StatusSync] ✅ Done! Synced: ${synced}, Skipped (no changes): ${skipped}`);

  return { synced, skipped };
}
